import { useState, useEffect, useCallback } from 'react';
import QuizInterface from '@/components/organisms/QuizInterface';
import { EventDisplayPage } from '@/features/event-display';
import BetaPartnerPage from '@/features/beta-partner';
import NewHomePage from '@/features/homepage';
import IndustryPage, { constructionContent, eventsContent, tvFilmContent, facilitiesContent } from '@/features/industry-page';
import { PricingPage, PricingUnimoBuildPage } from '@/features/pricing';
import { PrivacyPolicyPage, CookiePolicyPage, TermsOfServicePage, AccessibilityStatementPage } from '@/features/legal';
import { ContactPage } from '@/features/contact';
import { StubPage } from '@/features/resources';
import { UKConstructionWeekPage } from '@/features/uk-construction-week/UKConstructionWeekPage';
import { SecureProjectsPage } from '@/features/industries';
import { IndustryOnePagerPage, constructionConfig, eventsConfig, type IndustryOnePagerConfig } from '@/features/industry-one-pager';
import UkcwLandingPage from '@/features/ukcw-2026';

import { UKCWNECPage } from '@/features/ukcw-nec/UKCWNECPage';
import { trackPageView } from '@/lib/analytics';
import NotFoundPage from '@/components/organisms/NotFoundPage';

// Map of one-pager URL paths to their configs. Each entry adds a new
// industry-flavoured one-pager surface (campaign URLs separate from the
// /industries/* IA pages).
const ONE_PAGER_CONFIGS: Record<string, IndustryOnePagerConfig> = {
  [constructionConfig.route]: constructionConfig,
  [eventsConfig.route]: eventsConfig,
};

/**
 * Main application component for public, campaign and review routes.
 *
 * This component implements a simple client-side router that handles direct URL access
 * and navigation between different pages of the application.
 */
function App() {
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentPath, setCurrentPath] = useState(() => window.location.pathname);
  const [searchParams, setSearchParams] = useState(() => new URLSearchParams(window.location.search));

  // Handle route changes
  const handleRouteChange = useCallback((path: string, search: string = '') => {
    setCurrentPath(path);
    setSearchParams(new URLSearchParams(search));
  }, []);

  useEffect(() => {
    trackPageView(currentPath);
  }, [currentPath]);

  // Enhanced routing based on URL path
  useEffect(() => {
    // Listen for route changes
    const handleLocationChange = () => {
      handleRouteChange(window.location.pathname, window.location.search);
    };

    // Add event listeners for navigation
    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('pushstate', handleLocationChange);
    window.addEventListener('replacestate', handleLocationChange);

    // Custom event for programmatic navigation
    const handleCustomRouteChange = (event: Event) => {
      if (event instanceof CustomEvent) {
        handleRouteChange(event.detail.path, event.detail.search || '');
      }
    };

    window.addEventListener('routechange', handleCustomRouteChange);

    return () => {
      // Clean up event listeners
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('pushstate', handleLocationChange);
      window.removeEventListener('replacestate', handleLocationChange);
      window.removeEventListener('routechange', handleCustomRouteChange);
    };
  }, [handleRouteChange]);

  const handleChatClick = () => {
    setShowQuiz(true);
  };

  // Normalize path to handle trailing slashes
  const cleanPath = currentPath.replace(/\/+$/, '');
  // Existing review URLs remain compatible while canonical links use launch routes.
  const normalizedPath = cleanPath === '/new' ? '' : cleanPath.replace(/-new$/, '');

  if (normalizedPath === '/construction-twin') return <iframe title="unimo — Construction Process Twin" src="/experiences/construction-twin/index.html" style={{position:'fixed',inset:0,width:'100%',height:'100%',border:0}} allow="fullscreen" />;

  if (normalizedPath === '/ukcw-nec') return <UKCWNECPage />;

  // Route-based rendering
  // If the path is /event-display, show the event display page
  if (normalizedPath === '/event-display') {
    // Extract event type from URL parameters
    const eventType = searchParams.get('type') || 'default';
    return <EventDisplayPage eventType={eventType} />;
  }

  // If the path is /beta-partner, show the beta partner page
  if (normalizedPath === '/beta-partner') {
    return <BetaPartnerPage />;
  }

  // Redesigned launch pages; legacy review URLs normalize above.
  if (normalizedPath === '/uk-construction-week') {
    return <UKConstructionWeekPage />;
  }

  if (normalizedPath === '') {
    return <NewHomePage />;
  }

  // Redesigned industry, pricing, contact and policy pages.
  if (normalizedPath === '/industries/construction') {
    return <IndustryPage content={constructionContent} />;
  }
  if (normalizedPath === '/industries/events') {
    return <IndustryPage content={eventsContent} />;
  }
  if (normalizedPath === '/industries/tv-film') {
    return <IndustryPage content={tvFilmContent} />;
  }
  if (normalizedPath === '/industries/facilities') {
    return <IndustryPage content={facilitiesContent} />;
  }
  if (normalizedPath === '/pricing') {
    return <PricingPage />;
  }
  if (normalizedPath === '/pricing-unimobuild') {
    return <PricingUnimoBuildPage />;
  }
  if (normalizedPath === '/privacy') {
    return <PrivacyPolicyPage />;
  }
  if (normalizedPath === '/cookies') {
    return <CookiePolicyPage />;
  }
  if (normalizedPath === '/terms') {
    return <TermsOfServicePage />;
  }
  if (normalizedPath === '/accessibility') {
    return <AccessibilityStatementPage />;
  }
  if (normalizedPath === '/contact') {
    return <ContactPage />;
  }
  // UK Construction Week 2026 "Talk to an Expert" campaign landing page.
  // Standalone surface, no site nav — see docs/build-log.md.
  if (normalizedPath === '/ukcw2026') {
    return <UkcwLandingPage />;
  }
  // Resources nav stub pages — internal sign-off only, not real content.
  // See docs/build-log.md.
  if (normalizedPath === '/documentation') {
    return <StubPage title="Documentation" />;
  }
  if (normalizedPath === '/integrations') {
    return <StubPage title="Integrations" />;
  }
  if (normalizedPath === '/hardware') {
    return <StubPage title="Hardware and sensors" />;
  }
  if (normalizedPath === '/security') {
    return <StubPage title="Security" />;
  }

  // Industry one-pagers (campaign URLs). Standalone marketing surfaces —
  // no Navbar — but still get CookieConsent so the page-view analytics
  // fired by handleRouteChange has a consented session.
  const onePagerConfig = ONE_PAGER_CONFIGS[normalizedPath];
  if (onePagerConfig) {
    return (
      <>
        <IndustryOnePagerPage config={onePagerConfig} />

      </>
    );
  }

  if (normalizedPath === '/industries/secure-projects') {
    return (
      <>
        <SecureProjectsPage onChatClick={handleChatClick} />
        {showQuiz && (
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-8 z-50">
            <div className="bg-card rounded-lg w-full max-w-6xl h-full max-h-[90vh] overflow-hidden">
              <QuizInterface onClose={() => setShowQuiz(false)} />
            </div>
          </div>
        )}
      </>
    );
  }

  return <NotFoundPage />;
}

export default App;
